import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Heart, Share2, Star, Package, Shield, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductPage = ({ addToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [hasSelectedSize, setHasSelectedSize] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      // Don't auto-select a variant, let user choose
      setSelectedVariant(null);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getGalleryImages = () => {
    if (!product) return [];
    
    const images = [...product.image_urls];
    
    // Ensure we have exactly 4 images for the gallery
    while (images.length < 4) {
      // Duplicate existing images to fill gallery
      images.push(images[images.length % product.image_urls.length]);
    }
    
    return images.slice(0, 4); // Take only first 4 images
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      toast.error('Please select a size');
      return;
    }

    if (selectedVariant.stock_quantity < quantity) {
      toast.error('Not enough stock available');
      return;
    }

    addToCart(product, selectedVariant, quantity);
    toast.success(`Added ${quantity}x ${product.name} (Size ${selectedVariant.size}) to cart`);
  };

  const getTotalStock = () => {
    return product?.variants.reduce((total, variant) => total + variant.stock_quantity, 0) || 0;
  };

  const getStockStatus = (stock) => {
    if (stock === 0) return { class: 'text-red-600', text: 'Out of Stock', icon: AlertTriangle };
    if (stock === 1) return { class: 'text-amber-600', text: '1 in stock', icon: AlertTriangle };
    return { class: 'text-amber-600', text: 'Low Stock', icon: AlertTriangle };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center" data-testid="loading-indicator">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center" data-testid="product-not-found">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <Button onClick={() => navigate('/')} className="bg-gray-900 hover:bg-gray-800 text-white">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const stockStatus = getStockStatus(selectedVariant?.stock_quantity || 0);
  const StockIcon = stockStatus.icon;

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid={`product-page-${product.id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-8 hover:bg-gray-100"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Product Images */}
            <div className="p-8">
              {/* Main Image */}
              <div className="aspect-square mb-6 bg-gray-100 rounded-2xl overflow-hidden">
                <img
                  src={getGalleryImages()[selectedImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  data-testid="main-product-image"
                />
              </div>

              {/* Gallery - Always show 4 images */}
              <div className="grid grid-cols-4 gap-4">
                {getGalleryImages().map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square rounded-xl overflow-hidden border-2 transition-all duration-200 hover:scale-105 ${
                      selectedImageIndex === index ? 'border-gray-900 ring-2 ring-gray-900 ring-opacity-20' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    data-testid={`gallery-image-${index}`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Details */}
            <div className="p-8">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="category-badge" data-testid="product-category">
                    {product.category.replace('_', ' ')}
                  </span>
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm" data-testid="share-button">
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" data-testid="wishlist-button">
                      <Heart className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="product-title">
                  {product.name}
                </h1>
                
                <p className="text-lg text-gray-600" data-testid="product-brand">
                  by {product.brand}
                </p>
              </div>

              {/* Price and Stock - Only show stock status when size is selected */}
              <div className="flex items-center justify-between mb-6">
                <div className="price text-4xl" data-testid="product-price">
                  <span className="price-currency">$</span>
                  {product.price.toFixed(2)}
                </div>
                
                {hasSelectedSize && selectedVariant && (
                  <div className={`flex items-center space-x-2 ${stockStatus.class}`} data-testid="stock-status">
                    <StockIcon className="w-5 h-5" />
                    <span className="font-medium">{stockStatus.text}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="mb-8">
                <p className="text-gray-700 leading-relaxed" data-testid="product-description">
                  {product.description}
                </p>
              </div>

              {/* Size Selection */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Select Size</h3>
                  <button className="text-sm text-gray-500 hover:text-gray-700" data-testid="size-guide-button">
                    Size Guide
                  </button>
                </div>
                
                <div className="grid grid-cols-4 gap-3" data-testid="size-selector">
                  {product.variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => {
                        setSelectedVariant(variant);
                        setHasSelectedSize(true);
                        setQuantity(1); // Reset quantity when changing size
                      }}
                      disabled={variant.stock_quantity === 0}
                      className={`size-option ${
                        selectedVariant?.id === variant.id ? 'selected' : ''
                      } ${variant.stock_quantity === 0 ? 'unavailable' : ''}`}
                      data-testid={`size-${variant.size}`}
                    >
                      <div className="text-sm font-semibold">US {variant.size}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected Variant Details - REMOVED */}

              {/* Quantity Selection - Only show when size is selected */}
              {hasSelectedSize && selectedVariant && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quantity</h3>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center border border-gray-300 rounded-lg">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="p-3 hover:bg-gray-50 transition-colors"
                        data-testid="decrease-quantity"
                      >
                        <span className="text-lg font-medium">−</span>
                      </button>
                      <span className="px-6 py-3 text-lg font-medium" data-testid="quantity-display">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(Math.min(selectedVariant?.stock_quantity || 1, quantity + 1))}
                        disabled={!selectedVariant || quantity >= selectedVariant.stock_quantity}
                        className="p-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        data-testid="increase-quantity"
                      >
                        <span className="text-lg font-medium">+</span>
                      </button>
                    </div>
                    {selectedVariant && quantity >= selectedVariant.stock_quantity && selectedVariant.stock_quantity > 2 && (
                      <span className="text-sm text-amber-600" data-testid="max-quantity-warning">
                        Maximum quantity reached
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Add to Cart */}
              <div className="space-y-4">
                <Button
                  onClick={handleAddToCart}
                  disabled={!selectedVariant || (selectedVariant && selectedVariant.stock_quantity === 0)}
                  className="w-full bg-brand-purple hover:bg-brand-purple-dark text-white py-4 text-lg font-semibold disabled:bg-gray-400"
                  data-testid="add-to-cart-main"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {!selectedVariant 
                    ? 'Select a Size' 
                    : selectedVariant.stock_quantity === 0 
                    ? 'Gone' 
                    : `Lock It In - $${(product.price * quantity).toFixed(2)}`
                  }
                </Button>

                {/* Features */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-200">
                  <div className="text-center">
                    <Package className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 font-medium">Fast Delivery</p>
                    <p className="text-xs text-gray-500">2-3 business days</p>
                  </div>
                  <div className="text-center">
                    <Shield className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 font-medium">Authentic</p>
                    <p className="text-xs text-gray-500">100% Guaranteed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;